import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { renderForecastSvg } from '../../util/forecastChart';
import ForecastChartLarge from './ForecastChartLarge';

// The expanded panel renders (via portal) into a slot inserted right ABOVE the
// results row (`.home-result-container-new`), so it's a full-width section above
// the sidebar + rate cards, in normal flow — not an overlay/modal. The slot is
// created imperatively (anchored to the results container, which always exists
// once results render) rather than declared in a parent, which proved brittle.
const EXPAND_SLOT_ID = 'pf-forecast-expand-slot';
const RESULTS_ANCHOR = '.home-result-container-new';

/**
 * ForecastMiniChart — compact rate history + forecast for the left filter bar.
 *
 * Calls rate-api's /lane-forecast (via the same /api proxy pattern rate-search
 * already uses) and draws the dependency-free SVG from util/forecastChart.
 * Customer-safe: prefers `forecastSelling` (buying + the customer's profit add-on)
 * and never renders raw cost. Renders nothing until there's usable history, so
 * it degrades quietly on thin lanes.
 *
 * The card matches the other sidebar cards' width (box-sizing: border-box — the
 * app's reset is content-box, which otherwise pushed this card wider). Expanding
 * opens a full-width section above the results (see EXPAND_SLOT_ID), not a modal.
 *
 * Props:
 *   lane      { locationFromIds:number[], locationToIds:number[], mode, rateType, sizeKey }
 *   entityId  customer id -> selling view (optional)
 *   jwtToken  caller JWT (optional; server falls back to service token)
 *   endpoint  override URL (default '/api/lane-forecast')
 */
const ForecastMiniChart = ({ lane, entityId, jwtToken, endpoint = '/api/lane-forecast', title = 'Rate trend + forecast', laneName, anchorSellingPrice }) => {
  const [state, setState] = useState({ loading: true, history: [], forecast: null });
  const [expanded, setExpanded] = useState(false);
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' && window.innerWidth <= 768);
  const [slotEl, setSlotEl] = useState(null);
  const cancelled = useRef(false);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Find or create the full-width expand slot just before the results row. Done
  // imperatively so it works regardless of which container renders the layout.
  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    let slot = document.getElementById(EXPAND_SLOT_ID);
    let created = false;
    if (!slot) {
      const anchor = document.querySelector(RESULTS_ANCHOR);
      if (anchor?.parentElement) {
        slot = document.createElement('div');
        slot.id = EXPAND_SLOT_ID;
        anchor.parentElement.insertBefore(slot, anchor);
        created = true;
      }
    }
    setSlotEl(slot || null);
    return () => {
      if (created && slot?.parentElement) slot.remove();
    };
  }, []);

  useEffect(() => {
    cancelled.current = false;
    const ok = lane?.locationFromIds && lane?.locationToIds && lane?.mode;
    if (!ok) { setState({ loading: false, history: [], forecast: null }); return; }

    setState((s) => ({ ...s, loading: true }));
    fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      // Customer (entityId) -> their selling view; public/lead user (no entityId)
      // -> the public selling view. For public we send the cheapest displayed rate
      // (anchorSellingPrice) so rate-api applies that implied profit to the trend.
      body: JSON.stringify({
        ...lane,
        entityId,
        pricingView: entityId ? undefined : 'public',
        anchorSellingPrice: entityId ? undefined : anchorSellingPrice,
        jwtToken: jwtToken || 'pf-anon',
      }),
    })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error('forecast fetch failed'))))
      .then((d) => {
        if (cancelled.current) return;
        // Customer-safe: ONLY ever show the selling view (marked-up). Never fall
        // back to buying (d.history/d.forecast are cost) — that would leak margin.
        // (confidence is a label, not a price, so it's safe to read from either.)
        const confidence = d.forecastSelling?.confidence || d.forecast?.confidence || null;
        setState({ loading: false, history: d.historySelling || [], forecast: d.forecastSelling || null, confidence });
      })
      .catch(() => { if (!cancelled.current) setState({ loading: false, history: [], forecast: null }); });

    return () => { cancelled.current = true; };
  }, [endpoint, entityId, jwtToken, anchorSellingPrice, JSON.stringify(lane)]);

  const { loading, history, forecast } = state;
  if (loading) return null;                        // stay invisible until data lands
  const svg = renderForecastSvg({ history, forecast });
  // Thin lane -> render nothing (and no portal, so any stale panel clears too).
  if (!svg) return null;

  const confidence = forecast?.confidence || state.confidence || null;

  const expandedPanel = (
    <div
      // Layout (max-width / centering) lives in .forecast-expand-panel so the page
      // content width is defined once (--pageContentWidth, shared with .search-container).
      className="forecast-expand-panel"
      style={{ background: '#fff', borderRadius: 8, boxShadow: '0 1px 6px rgba(0,0,0,0.15)', padding: isMobile ? '14px 14px 18px' : '16px 22px 20px' }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <span style={{ font: '600 14px sans-serif', color: '#43637c' }}>
          Rate history &amp; forecast{laneName ? ` — ${laneName}` : ''}
        </span>
        <button
          type="button"
          onClick={() => setExpanded(false)}
          style={{ font: '600 11px ui-monospace, monospace', color: '#16788B', background: 'transparent', border: 'none', cursor: 'pointer', padding: 4 }}
        >
          collapse ✕
        </button>
      </div>
      <ForecastChartLarge history={history} forecast={forecast} laneName={laneName} confidence={confidence} mobile={isMobile} />
      <div style={{ font: '11px ui-monospace, monospace', color: '#8494A6', marginTop: 12, textAlign: 'center' }}>
        Your selling rate — history and projected forecast for this lane.
      </div>
    </div>
  );

  return (
    <>
      {/* Native <button> (not a div+role) for accessibility; inline-span children
          keep the markup valid inside a button. Click toggles the expanded panel.
          Width comes from the .forecast-mini CSS class (matches .role-container-type
          exactly: 270px desktop, 100% mobile) so it lines up with the sidebar cards
          — an inline width:100% filled the wider flex column and overflowed them. */}
      <button
        type="button"
        className="forecast-mini"
        aria-expanded={expanded}
        onClick={() => setExpanded((e) => !e)}
        title={expanded ? 'Click to collapse' : 'Click to expand'}
        style={{ boxSizing: 'border-box', display: 'block', textAlign: 'left', background: '#fff', border: 'none', borderRadius: 5, boxShadow: '0 1px 5px rgba(0,0,0,0.15)', padding: 12, marginBottom: 15, cursor: 'pointer', font: 'inherit' }}
      >
        <span style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
          <span style={{ font: '600 10px ui-monospace, monospace', letterSpacing: '0.1em', textTransform: 'uppercase', color: '#56697E' }}>{title}</span>
          <span style={{ font: '10px ui-monospace, monospace', color: '#16788B' }}>{expanded ? 'collapse ⤡' : 'expand ⤢'}</span>
        </span>
        <span style={{ display: 'block' }} dangerouslySetInnerHTML={{ __html: svg }} />
        <span style={{ display: 'flex', gap: 12, font: '10px ui-monospace, monospace', color: '#56697E', marginTop: 6 }}>
          <span><span style={{ display: 'inline-block', width: 12, borderTop: '2px solid #16788B', verticalAlign: 'middle', marginRight: 4 }} />history</span>
          <span><span style={{ display: 'inline-block', width: 12, borderTop: '2px dashed #B87514', verticalAlign: 'middle', marginRight: 4 }} />forecast</span>
        </span>
      </button>

      {expanded && slotEl && createPortal(expandedPanel, slotEl)}
    </>
  );
};

export default ForecastMiniChart;
